import { useFloating, useHover, useInteractions } from '@floating-ui/react';
import { animated, SpringConfig, to, useSpring, useSpringRef, useTransition } from '@react-spring/web';
import classNames from 'classnames';
import clamp from 'lodash/clamp';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DraggableData, DraggableEvent } from 'react-draggable';
import { Nullable } from 'src/types/nullable.type';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { NeueAddSectionButton } from './add-section-button';
import { useEditorStore } from './editor-store';
import { getSuperRect } from './helpers/get-super-rect';
import { SectionCard } from './section-card';
import { Block, BlockRenderElement, Rect, Section } from './types';
import { shallow } from 'zustand/shallow';
import { useUpdateEffect } from 'react-use';
import { usePreviousValue } from 'src/utils/react/previous-value.hook';
import { getHtmlText } from './helpers/get-html-text';
import { createPropsEqualsChecker } from './utils/create-props-equals-checker';
import throttle from 'lodash/throttle';
import { isReadonly } from './native-e-signature/utils';
import { MemoizedEditorCurrentSectionUpdater } from './editor-current-section-updater';
import { each, onScroll } from '@react-spring/shared';
import { useEditorContext } from 'src/store/editor';

export const WIDTH_ANIMATION_CONFIG: SpringConfig = {
  mass: 1,
  tension: 400,
  friction: 40,
};

type DragState = {
  draggingCardId: Nullable<Section['id']>;
  dragged: boolean;
  draggingCardIndex: number;
  newDraggingCardIndex: number;
  startX: number;
  startY: number;
  dx: number;
  dy: number;
  x: number;
  y: number;
  stopped: boolean;
};

type SectionCardData = {
  section: Section;
  // title: string;
  userProvidedTitle: Nullable<string>;
  // placeholder: boolean;
  blockRenderElements: BlockRenderElement[];
};

type SectionCardTransitionItem = {
  sectionCardData: SectionCardData;
  rect: Rect;
};

const SECTION_BAR_LAYOUT = {
  cardWidth: 272 + 24,
  cardHeight: 42,
  cardGap: 10,
  barMaxHeight: 520,
  barPadding: 4,
};

const DRAG_THRESHOLD = 5;

export const SectionBar = () => {
  const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);
  const setContextMenuState = useEditorStore((state) => state.setContextMenuState);
  const setCurrentSectionId = useEditorStore((state) => state.setCurrentSectionId);
  const setScrollToSectionId = useEditorStore((state) => state.setScrollToSectionId);
  const setIsDraggingSomethingOnEditor = useEditorStore((state) => state.setIsDraggingSomethingOnEditor);

  // Only directly use from editorStore if it's a critical for the state to reflect immediately
  // For e.g. context menu state
  const contextMenuState = useEditorStore((state) => state.contextMenuState);
  const sectionDrawerOpen = useEditorStore((state) => state.sectionDrawerOpen);
  const currentSectionId = useEditorStore((state) => state.currentSectionId);
  const layout = useEditorStore((state) => state.layout, shallow);
  const contextMenuStateActiveSectionId =
    contextMenuState?.entity.type === 'section-card' ? contextMenuState?.entity.sectionId : null;
  const layoutStage = useEditorStore((state) => state.layoutStage);

  const journey = useEditorContext((state) => state.journey);
  const { is_folders_enabled: foldersEnabled } = journey;

  type LocalNonCriticalEditorState = Pick<ReturnType<typeof useEditorStore.getState>, 'isDraggingSomethingOnEditor'>;

  const [localNonCriticalEditorState, setLocalNonCriticalEditorState] = useState<LocalNonCriticalEditorState>(
    useEditorStore.getState()
  );
  const throttleSetLocalNonCriticalEditorStateRef = React.useRef(
    throttle(setLocalNonCriticalEditorState, 1000, { leading: true, trailing: true })
  );

  const { isDraggingSomethingOnEditor } = localNonCriticalEditorState;

  React.useEffect(() => {
    const unsubscribe = useEditorStore.subscribe(
      (state) => {
        return {
          isDraggingSomethingOnEditor: state.isDraggingSomethingOnEditor,
        };
      },
      throttleSetLocalNonCriticalEditorStateRef.current,
      { equalityFn: shallow }
    );
    return () => {
      throttleSetLocalNonCriticalEditorStateRef.current.cancel();
      unsubscribe();
    };
  }, []);

  const pullNonCriticalEditorStateImmediately = useRefCallback(() => {
    setLocalNonCriticalEditorState(useEditorStore.getState() as LocalNonCriticalEditorState);
  }, []);

  const enableAnimationRef = useRef(false);

  const { sections, renderElements, sectionLayoutInfos, innerActualHeight } = layout;

  const readonly = isReadonly(sections);

  const prevSectionsLength = usePreviousValue(sections.length);

  const [hovering, setHovering] = useState(false);
  const { refs, context } = useFloating({
    open: hovering,
    onOpenChange: setHovering,
  });
  const hover = useHover(context);
  const { getReferenceProps } = useInteractions([hover]);

  const renderElementsMap = useMemo(() => {
    const map = new Map<Block['id'], BlockRenderElement>();
    for (const re of renderElements) {
      if (re.type === 'block' && re.block.id) {
        map.set(re.block.id, re);
      }
    }
    return map;
  }, [renderElements]);

  useUpdateEffect(() => {
    if (typeof prevSectionsLength !== 'undefined' && prevSectionsLength !== sections.length) {
      enableAnimationRef.current = true;
    }
  }, [prevSectionsLength, sections.length]);

  const sectionCardData: SectionCardData[] = useMemo(
    () =>
      sections.map((section) => {
        let userProvidedTitle = section.name;
        const blockRenderElements = section.blocks
          .map((block) => renderElementsMap.get(block.id))
          .filter(Boolean) as BlockRenderElement[];

        return {
          section,
          userProvidedTitle,
          blockRenderElements,
        };
      }),
    [sections, renderElementsMap]
  );

  const onSectionCardClick = (id: Section['id']) => {
    setCurrentSectionId(id);
    setScrollToSectionId(id);
    pullNonCriticalEditorStateImmediately();
  };

  const api = useSpringRef();
  const [props] = useSpring(
    () => ({
      ref: api,
      from: { width: 64, expandedFraction: 0 },
      to: { width: 280, expandedFraction: 1 },
    }),
    []
  );

  const [sectionDrawerCompletelyClosed, setSectionDrawerCompletelyClosed] = useState(!sectionDrawerOpen);

  const onStartCallback = useRefCallback(() => {
    setSectionDrawerCompletelyClosed(false);
  }, []);

  const onRestCallback = useRefCallback(() => {
    if (!sectionDrawerOpen) {
      setSectionDrawerCompletelyClosed(true);
    }
  }, [sectionDrawerOpen]);

  useEffect(() => {
    if (sectionDrawerOpen) {
      api.start({
        expandedFraction: 1,
        config: WIDTH_ANIMATION_CONFIG,
        onStart: onStartCallback,
      });
    } else {
      api.start({
        expandedFraction: 0,
        config: WIDTH_ANIMATION_CONFIG,
        onRest: onRestCallback,
      });
    }
  }, [sectionDrawerOpen, onStartCallback, onRestCallback]);

  const [dragState, setDragState] = useState<DragState>({
    draggingCardId: null,
    dragged: false,
    draggingCardIndex: -1,
    newDraggingCardIndex: -1,
    startX: 0,
    startY: 0,
    dx: 0,
    dy: 0,
    x: 0,
    y: 0,
    stopped: false,
  });

  const itemsRef = useRef(new Map<Section['id'], HTMLDivElement>());

  let y = SECTION_BAR_LAYOUT.barPadding;
  const sectionCardRects: Rect[] = useMemo(() => {
    const rects: Rect[] = [];
    for (let i = 0; i < sections.length; i++) {
      const rect = {
        x: 0,
        y,
        width: SECTION_BAR_LAYOUT.cardWidth,
        height: SECTION_BAR_LAYOUT.cardHeight,
      };
      rects.push(rect);
      y += rect.height;
      if (i < sections.length - 1) {
        y += SECTION_BAR_LAYOUT.cardGap;
      }
    }
    return rects;
  }, [sections]);

  let { width: totalWidth, height: totalHeight } = getSuperRect(sectionCardRects);

  const onCardDragStart = (id: Section['id'], e: DraggableEvent, args: DraggableData) => {
    e.preventDefault();
    enableAnimationRef.current = true;
    const { x, y } = args;
    let { clientX, clientY, offsetY } = e as MouseEvent;
    setDragState((state) => ({
      draggingCardId: id,
      draggingCardIndex: sections.findIndex((section) => section.id === id),
      dragged: false,
      newDraggingCardIndex: -1,
      startX: x,
      startY: y,
      dx: 0,
      dy: 0,
      x: 0,
      y: clientY,
      stopped: false,
    }));
    setIsDraggingSomethingOnEditor(true);
    transitionApi.start();
  };

  const onCardDrag = (id: Section['id'], e: DraggableEvent, args: DraggableData) => {
    const { node, x, y } = args;
    const { draggingCardId, draggingCardIndex, startX, startY } = dragState;
    if (draggingCardId === null) return;
    if (id !== draggingCardId) return;
    let { clientX, clientY } = e as MouseEvent;
    const dy = y - dragState.startY;
    const dragged = dragState.dragged || Math.abs(dy) > DRAG_THRESHOLD;
    const newY = clamp(
      sectionCardRects[draggingCardIndex].y + y - startY,
      SECTION_BAR_LAYOUT.barPadding,
      totalHeight - SECTION_BAR_LAYOUT.cardHeight
    );
    clientY = newY + clientY - node.getBoundingClientRect().y;

    setDragState((state) => ({
      ...state,
      dragged,
      x,
      y,
      dx: x - startX,
      dy: newY - sectionCardRects[draggingCardIndex].y,
    }));

    if (!dragged) {
      transitionApi.start();
      return;
    }

    let newIndex = -1;
    if (clientY < SECTION_BAR_LAYOUT.barPadding) {
      newIndex = 0;
    } else if (clientY > totalHeight) {
      newIndex = sections.length - 1;
    } else {
      for (let i = 0; i < sectionCardRects.length; i++) {
        const rect = sectionCardRects[i];
        if (!rect) continue;
        if (clientY >= rect.y) {
          newIndex = i;
        }
        if (clientY <= rect.y + rect.height) {
          break;
        }
      }
    }
    setDragState((state) => ({
      ...state,
      newDraggingCardIndex: newIndex,
    }));
    transitionApi.start();
  };

  const onCardDragStop = (id: Section['id']) => {
    const { draggingCardId, dragged, draggingCardIndex, newDraggingCardIndex } = dragState;
    if (draggingCardId === null) return;
    if (id !== draggingCardId) return;
    if (newDraggingCardIndex !== -1 && newDraggingCardIndex !== draggingCardIndex) {
      if (dragged) {
        setDragState((state) => ({
          ...state,
          x: 0,
          y: 0,
          stopped: true,
          dragged: false,
        }));
        dispatchUserEditorAction({
          type: 'move-section',
          id: draggingCardId,
          newIndex: newDraggingCardIndex,
        });
      }
    }
    enableAnimationRef.current = false;
    setDragState((state) => ({
      ...state,
      x: 0,
      y: 0,
      stopped: true,
      dragged: false,
    }));
    setIsDraggingSomethingOnEditor(false);
    pullNonCriticalEditorStateImmediately();
    transitionApi.start();
  };

  const transitionData = useMemo(
    () =>
      sectionCardData.map((sectionCardDataItem, i) => {
        return {
          sectionCardData: sectionCardDataItem,
          rect: sectionCardRects[i],
        };
      }),
    [sectionCardData, sectionCardRects]
  );

  const [transitions, transitionApi] = useTransition<SectionCardTransitionItem, { y: number }>(transitionData, () => ({
    config: { mass: 1, tension: 400, friction: 40 },
    key: (item: SectionCardTransitionItem) => item.sectionCardData?.section?.id,
    from: (item: SectionCardTransitionItem) => {
      // console.log('transition from', item);
      return item.sectionCardData?.section?.id === dragState.draggingCardId
        ? {
            y: item.rect.y + dragState.dy,
          }
        : {
            y: item.rect.y,
          };
    },
    update: (item: SectionCardTransitionItem, i: number) => {
      if (isDraggingSomethingOnEditor && !enableAnimationRef.current) {
        return;
      }
      // console.log('transition update', item);
      const { draggingCardId, draggingCardIndex, newDraggingCardIndex, stopped } = dragState;
      if (item.sectionCardData?.section?.id === draggingCardId) {
        if (stopped) {
          return {
            y: item.rect.y,
            immediate: false,
          };
        } else {
          return {
            y: item.rect.y + dragState.dy,
            immediate: true,
          };
        }
      }
      // const draggingCardRect =
      //   draggingCardId === null ? undefined : itemsRef.current.get(draggingCardId)?.getBoundingClientRect();
      const draggingCardRect = draggingCardId ? sectionCardRects[draggingCardIndex] : undefined;
      let dy = 0;
      if (
        draggingCardRect &&
        newDraggingCardIndex !== -1 &&
        draggingCardIndex !== null &&
        draggingCardIndex !== i &&
        !stopped
      ) {
        if (draggingCardIndex > i && newDraggingCardIndex <= i) {
          dy = draggingCardRect.height + SECTION_BAR_LAYOUT.cardGap;
        } else if (draggingCardIndex < i && newDraggingCardIndex >= i) {
          dy = -draggingCardRect.height - SECTION_BAR_LAYOUT.cardGap;
        }
      }
      return {
        y: item.rect.y + dy,
        immediate: false,
      };
    },
  }));

  useUpdateEffect(() => {
    if (!isDraggingSomethingOnEditor && enableAnimationRef.current) {
      transitionApi.start();
    }
  }, [sections, dragState, isDraggingSomethingOnEditor]);

  const containerRef = React.useRef<HTMLDivElement>(null!);

  // Based on code in
  // https://github.com/pmndrs/react-spring/blob/6c72cb9e3789ba64a4e2a503dfce227ee646f3cc/packages/core/src/hooks/useScroll.ts
  // Can't use it as is, because it doesn't apply on scroll effect immediately
  const [scrollValues, scrollValuesApi] = useSpring(
    () => ({
      scrollTopFadeOpacity: 0,
      scrollBottomFadeOpacity: 0,
    }),
    []
  );

  React.useEffect(() => {
    const cleanupScroll = onScroll(
      ({ y }) => {
        const maxContainerScrollTop = containerRef.current.scrollHeight - containerRef.current.clientHeight;
        scrollValuesApi.start({
          scrollTopFadeOpacity: y.current > 24 ? 1 : y.current / 24,
          scrollBottomFadeOpacity:
            y.current < maxContainerScrollTop - 24 ? 1 : (maxContainerScrollTop - y.current) / 24,
          immediate: true,
        });
      },
      { container: containerRef.current }
    );

    return () => {
      /**
       * Stop the springs on unmount.
       */
      each(Object.values(scrollValues), (value) => value.stop());

      cleanupScroll();
    };
  }, []);

  const renderScrollAnimationOnTop = () => {
    if (foldersEnabled) {
      return null;
    }

    return (
      <>
        {' '}
        <animated.div
          className='absolute h-12 left-0 top-0 right-0 pointer-events-none bg-gradient-to-b from-neue-canvas-bg to-transparent'
          style={{
            opacity: scrollValues.scrollTopFadeOpacity,
          }}
        />
        <animated.div
          className='absolute h-12 left-0 bottom-0 right-0 pointer-events-none bg-gradient-to-t from-neue-canvas-bg to-transparent'
          style={{
            opacity: scrollValues.scrollBottomFadeOpacity,
          }}
        />
      </>
    );
  };

  return (
    <>
      <animated.div
        className={classNames(
          'pointer-events-auto flex flex-col gap-2 justify-start transition text-neue-journey-fg rounded-lg relative',
          'select-auto my-auto max-h-full',
          {
            'selection:bg-none selection:text-none': isDraggingSomethingOnEditor,
            'pb-1': !readonly,
          }
        )}
        style={{
          width: `${(sectionDrawerCompletelyClosed ? 64 : 280) + 24}px`,
          opacity: layoutStage === 'ready' ? 1 : 0,
        }}
      >
        <div className={classNames('relative flex flex-col flex-1 rounded-lg overflow-hidden isolate justify-center')}>
          <div
            className='relative overflow-y-auto scrollbar-hidden'
            style={{
              width: `${totalWidth}px`,
            }}
            ref={(el) => {
              refs.setReference(el);
              containerRef.current = el!;
            }}
            {...getReferenceProps()}
          >
            <div
              className='relative'
              style={{
                width: `${totalWidth}px`,
                height: `${totalHeight + 2 * SECTION_BAR_LAYOUT.barPadding}px`,
              }}
            >
              {transitions((style, item, _, i) => {
                const {
                  sectionCardData: {
                    section: { id },
                    userProvidedTitle,
                    blockRenderElements,
                  },
                } = item;
                return (
                  <SectionCard
                    key={id}
                    ref={(el) => {
                      if (el && id) {
                        itemsRef.current.set(id, el);
                      } else if (id) {
                        itemsRef.current.delete(id);
                      }
                    }}
                    readonly={readonly}
                    style={style}
                    rect={item.rect}
                    id={id}
                    index={i}
                    expandedFraction={props.expandedFraction}
                    sectionDrawerOpen={sectionDrawerOpen}
                    showIndices={hovering || (dragState.draggingCardId !== null && !dragState.stopped)}
                    userProvidedTitle={userProvidedTitle}
                    current={id === currentSectionId}
                    blockRenderElements={blockRenderElements}
                    dragging={dragState.draggingCardId === id && dragState.dragged}
                    elevated={dragState.draggingCardId === id && !dragState.stopped}
                    focused={contextMenuStateActiveSectionId === id}
                    onCardClick={() => onSectionCardClick(id)}
                    onCardDragStart={(e, args) => onCardDragStart(id, e, args)}
                    onCardDrag={(e, args) => onCardDrag(id, e, args)}
                    onCardDragStop={() => onCardDragStop(id)}
                    onNameChange={(name) => {
                      dispatchUserEditorAction({
                        type: 'set-section-name',
                        id,
                        name: getHtmlText(name),
                        setManually: true,
                      });
                    }}
                    setContextMenuState={(pageX, pageY, renameAction) =>
                      setContextMenuState({
                        entity: {
                          type: 'section-card',
                          sectionId: id,
                          renameAction,
                        },
                        pageX,
                        pageY,
                      })
                    }
                  />
                );
              })}
            </div>
          </div>
          {renderScrollAnimationOnTop()}
        </div>
        {!readonly && (
          <div className='relative left-6 flex items-center justify-start'>
            <NeueAddSectionButton forceRefreshSectionBar={pullNonCriticalEditorStateImmediately} />
          </div>
        )}
      </animated.div>
      <MemoizedEditorCurrentSectionUpdater
        sectionCardRects={sectionCardRects}
        scrollContainerRef={refs.reference as React.RefObject<HTMLDivElement>}
      />
    </>
  );
};

export const MemoizedSectionBar = React.memo(SectionBar, createPropsEqualsChecker([], 'SectionBar'));
